import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosWithAuth from '../../UtilsRedux/axiosWithAuth';
import { API_ROUTES } from '../../Constants/apiRoutes';
import { showFeedback } from '../../UI/uiFeedbackSlice';
import { fetchExpensesByProject } from '../Expense/expenseSlice';

// 🔄 Async Thunks

export const fetchMaterialsByProject = createAsyncThunk(
  'materials/fetchByProject',
  async (projectId, { rejectWithValue, dispatch }) => {
    try {
      const res = await axiosWithAuth.get(API_ROUTES.GET_MATERIALS_BY_PROJECT(projectId));
      return res.data.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to fetch materials';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Could not load materials',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);

export const fetchMaterialDetails = createAsyncThunk(
  'materials/fetchById',
  async (materialId, { rejectWithValue, dispatch }) => {
    try {
      const res = await axiosWithAuth.get(API_ROUTES.GET_MATERIAL_BY_ID(materialId));
      return res.data.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to fetch material';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Could not load material',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);

export const fetchMaterialOverview = createAsyncThunk(
  'materials/fetchOverview',
  async (projectId, { rejectWithValue, dispatch }) => {
    try {
      const res = await axiosWithAuth.get(API_ROUTES.GET_MATERIAL_OVERVIEW(projectId));
      return res.data.data; // { totalMaterials, lowStockItems, pendingBills, totalSpend }
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to fetch overview';

      dispatch(
        showFeedback({
          type: 'warning',
          title: 'Could not load material overview',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);

export const createMaterial = createAsyncThunk(
  'materials/create',
  async ({ formData, projectId }, { rejectWithValue, dispatch }) => {
    try {
      const res = await axiosWithAuth.post(API_ROUTES.CREATE_MATERIAL, {
        ...formData,
        projectId,
      });

      const created = res.data.data;

      // ✅ this is what you were missing
      if (projectId) {
        await dispatch(fetchMaterialsByProject(projectId));
        await dispatch(refreshMaterialMeta(projectId));
      }

      return created;
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to add material';
      dispatch(showFeedback({ type: 'error', title: 'Material creation failed', message }));
      return rejectWithValue(message);
    }
  }
);

export const receiveMaterial = createAsyncThunk(
  'materials/receive',
  async ({ materialId, quantity, projectId }, { rejectWithValue, dispatch }) => {
    try {
      const res = await axiosWithAuth.post(API_ROUTES.RECEIVE_MATERIAL(materialId), { quantity });
      dispatch(
        showFeedback({
          type: 'success',
          title: 'Stock updated',
          message: 'Material receipt has been recorded.',
        })
      );

      if (projectId) await dispatch(refreshMaterialMeta(projectId));

      return res.data.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to receive material';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Receive failed',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);

export const uploadMaterialBill = createAsyncThunk(
  'materials/uploadBill', // (string can be anything, but keep it consistent)
  async ({ materialId, projectId, ...rest }, { dispatch, rejectWithValue }) => {
    try {
      // body that goes to backend
      const requestBody = {
        projectId,     // required by backend
        ...rest,       // amount, quantity, fileUrl, date, paymentMethod, etc.
      };

      const res = await axiosWithAuth.post(
        API_ROUTES.UPLOAD_MATERIAL_BILL(materialId),
        requestBody
      );

      const payload = res.data.data; // { ...billFields, expenseCreated, expenseId, material }


      // 🔁 refresh expenses if we got a valid projectId
      if (projectId) {
        dispatch(
          fetchExpensesByProject({
            projectId,
            page: 1,
            pageSize: 20,
            filters: {},
          })
        );
      }

      // toast
      dispatch(
        showFeedback({
          type: 'success',
          title: 'Bill uploaded',
          message: payload.expenseCreated
            ? 'Bill uploaded and Expense added.'
            : 'Bill uploaded.',
        })
      );

      if (projectId) await dispatch(refreshMaterialMeta(projectId));


      return payload;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to upload material bill';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Bill upload failed',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);



export const logMaterialUsage = createAsyncThunk(
  'materials/logMaterialUsage',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const { materialId, projectId, ...rest } = data;
      const res = await axiosWithAuth.post(API_ROUTES.LOG_MATERIAL_USAGE(materialId), rest);

      dispatch(showFeedback({
        type: 'success',
        title: 'Usage logged',
        message: 'Material usage has been recorded.',
      }));


      if (projectId) await dispatch(refreshMaterialMeta(projectId));
      return res.data.data; // ✅ MUST be updated material
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to log usage';
      dispatch(showFeedback({ type: 'error', title: 'Usage logging failed', message }));
      return rejectWithValue(message);
    }
  }
);




export const undoMaterialUsage = createAsyncThunk(
  'materials/undoMaterialUsage',
  async ({ materialId, projectId }, { rejectWithValue, dispatch }) => {
    try {
      const res = await axiosWithAuth.delete(API_ROUTES.UNDO_MATERIAL_USAGE(materialId));
      dispatch(
        showFeedback({
          type: 'success',
          title: 'Usage reverted',
          message: 'The last material usage has been undone.',
        })
      );
      if (projectId) await dispatch(refreshMaterialMeta(projectId));
      return res.data.data; // updated material
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to undo material usage';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Undo usage failed',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);



export const undoMaterialReceive = createAsyncThunk(
  'materials/undoMaterialReceive',
  async ({ materialId, projectId }, { rejectWithValue, dispatch }) => {
    try {
      const res = await axiosWithAuth.post(API_ROUTES.UNDO_MATERIAL_RECEIVE(materialId));
      dispatch(
        showFeedback({
          type: 'success',
          title: 'Receipt reverted',
          message: 'The last material receipt has been undone.',
        })
      );
      if (projectId) await dispatch(refreshMaterialMeta(projectId));
      return res.data.data; // updated material
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to undo material receive';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Undo receive failed',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);



export const editMaterialDetails = createAsyncThunk(
  'materials/editDetails',
  async ({ materialId, updates, projectId }, { rejectWithValue, dispatch }) => {
    try {
      const res = await axiosWithAuth.patch(API_ROUTES.EDIT_MATERIAL(materialId), updates);
      dispatch(
        showFeedback({
          type: 'success',
          title: 'Material updated',
          message: 'Changes have been saved.',
        })
      );



      if (projectId) await dispatch(refreshMaterialMeta(projectId));
      return res.data.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to update material';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Update failed',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);





export const fetchRecentMaterialActivity = createAsyncThunk(
  'materials/fetchRecentActivity',
  async ({ projectId, limit = 4 }, { rejectWithValue, dispatch }) => {
    try {
      const qs = `types=material_receive,material_usage,material_bill&limit=${limit}`;
      const res = await axiosWithAuth.get(API_ROUTES.GET_PROJECT_ACTIVITY(projectId, qs));
      return res.data.data; // { items, nextCursor }
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to fetch activity';

      dispatch(
        showFeedback({
          type: 'warning',
          title: 'Could not load material activity',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);

export const fetchMoreMaterialActivity = createAsyncThunk(
  'materials/fetchMoreActivity',
  async ({ projectId, cursor, limit = 4 }, { rejectWithValue }) => {
    try {
      const qs = new URLSearchParams({
        types: 'material_receive,material_usage,material_bill',
        limit: String(limit),
        cursorCreatedAt: cursor.cursorCreatedAt,
        cursorId: cursor.cursorId,
      }).toString();
      const res = await axiosWithAuth.get(API_ROUTES.GET_PROJECT_ACTIVITY(projectId, qs));
      return res.data.data; // { items, nextCursor }
    } catch (err) {
      // no popup for “load more” to avoid spam
      return rejectWithValue(
        err?.response?.data?.message || 'Failed to fetch more activity'
      );
    }
  }
);



// 🗑️ Soft-delete (archive)
export const deleteMaterialById = createAsyncThunk(
  'materials/deleteMaterialById',
  async ({ materialId, projectId }, { rejectWithValue, dispatch }) => {
    try {
      await axiosWithAuth.delete(API_ROUTES.DELETE_MATERIAL(materialId));

      dispatch(
        showFeedback({
          type: 'success',
          title: 'Material archived',
          message: 'The material has been moved to archive.',
        })
      );
      // refresh list so counts/overview stay right
      if (projectId) dispatch(fetchMaterialsByProject(projectId));
      return { materialId };
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to delete material';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Archive failed',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);

// ♻️ Restore soft-deleted
export const restoreMaterialById = createAsyncThunk(
  'materials/restoreMaterialById',
  async ({ materialId, projectId }, { rejectWithValue, dispatch }) => {
    try {
      await axiosWithAuth.patch(API_ROUTES.RESTORE_MATERIAL(materialId));

      dispatch(
        showFeedback({
          type: 'success',
          title: 'Material restored',
          message: 'The material has been restored.',
        })
      );

      if (projectId) dispatch(fetchMaterialsByProject(projectId));
      return { materialId };
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to restore material';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Restore failed',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);

// 💣 Hard-delete (admin only)
export const hardDeleteMaterialById = createAsyncThunk(
  'materials/hardDeleteMaterialById',
  async ({ materialId, projectId }, { rejectWithValue, dispatch }) => {
    try {
      await axiosWithAuth.delete(API_ROUTES.HARD_DELETE_MATERIAL(materialId));

      dispatch(
        showFeedback({
          type: 'success',
          title: 'Material permanently deleted',
          message: 'This material has been removed permanently.',
        })
      );

      if (projectId) dispatch(fetchMaterialsByProject(projectId));
      return { materialId };
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        'Failed to permanently delete material';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Delete failed',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);



export const refreshMaterialMeta = (projectId) => async (dispatch) => {
  if (!projectId) return;
  await Promise.allSettled([
    dispatch(fetchMaterialOverview(projectId)),
    dispatch(fetchRecentMaterialActivity({ projectId, limit: 4 })),
  ]);
};





// 🧩 Slice

const materialSlice = createSlice({
  name: 'materials',
  initialState: {
    materials: [],
    materialDetails: null,
    materialDetailsLoading: false,
    materialDetailsError: null,
    selectedMaterial: null,
    summary: null,
    loading: false,
    submitting: false,
    error: null,


    overview: null,
    overviewLoading: false,


    // 👇 recent activity
    recentActivity: [],
    recentActivityNextCursor: null,
    recentActivityLoading: false,
  },
  reducers: {
    clearMaterialError: (state) => {
      state.error = null;
    },
    clearSelectedMaterial: (state) => {
      state.selectedMaterial = null;
    },
    clearMaterialDetails: (state) => {
      state.materialDetails = null;
      state.materialDetailsLoading = false;
      state.materialDetailsError = null;
    },
    clearMaterialSubmitState: (state) => {
      state.submitting = false;
      state.error = null;
    },

    resetRecentMaterialActivity: (state) => {
      state.recentActivity = [];
      state.recentActivityNextCursor = null;
      state.recentActivityLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // 🔄 Fetch all
      .addCase(fetchMaterialsByProject.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMaterialsByProject.fulfilled, (state, action) => {
        state.loading = false;
        state.materials = action.payload;
      })
      .addCase(fetchMaterialsByProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // 🔍 Fetch single
      .addCase(fetchMaterialDetails.pending, (state) => {
        state.materialDetailsLoading = true;
        state.materialDetailsError = null;
      })
      .addCase(fetchMaterialDetails.fulfilled, (state, action) => {
        state.materialDetails = action.payload;
        state.materialDetailsLoading = false;
      })
      .addCase(fetchMaterialDetails.rejected, (state, action) => {
        state.materialDetailsLoading = false;
        state.materialDetailsError = action.error.message;
      })

      // 📊 Overview
      .addCase(fetchMaterialOverview.pending, (state) => {
        state.overviewLoading = true;
      })
      .addCase(fetchMaterialOverview.fulfilled, (state, action) => {
        state.overviewLoading = false;
        state.overview = action.payload; // { totalMaterials, lowStockItems, pendingBills, totalSpend }
      })
      .addCase(fetchMaterialOverview.rejected, (state, action) => {
        state.overviewLoading = false;
        state.error = action.payload;
      })

      // ➕ Create
      .addCase(createMaterial.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createMaterial.fulfilled, (state, action) => {
        state.submitting = false;
        const created = action.payload;

        if (!created?._id) return;

        // prevent duplicates
        const exists = state.materials.some((m) => m._id === created._id);
        if (!exists) {
          // put on top (or push at end if you prefer)
          state.materials = [created, ...state.materials];
        }

        // optional: if drawer is open and is same material
        if (state.materialDetails?._id === created._id) {
          state.materialDetails = { ...state.materialDetails, ...created };
        }
      })
      .addCase(createMaterial.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })
      // 📥 Receive
      .addCase(receiveMaterial.fulfilled, (state, action) => {
        const updated = action.payload;
        state.materials = state.materials.map((mat) =>
          mat._id === updated._id ? updated : mat
        );
      })
      .addCase(receiveMaterial.rejected, (state, action) => {
        state.error = action.payload;
      })

      // 📎 Upload Bill
      .addCase(uploadMaterialBill.pending, (state) => {
        state.submitting = true;
      })
      .addCase(uploadMaterialBill.fulfilled, (state, action) => {
        state.submitting = false;

        const updatedMat = action.payload?.material; // ✅ backend returns this
        if (!updatedMat?._id) return;

        // update list
        state.materials = state.materials.map((m) =>
          m._id === updatedMat._id ? { ...m, ...updatedMat } : m
        );

        // update drawer details if same material is open
        if (state.materialDetails?._id === updatedMat._id) {
          state.materialDetails = { ...state.materialDetails, ...updatedMat };
        }
      })
      .addCase(uploadMaterialBill.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })

      .addCase(logMaterialUsage.pending, (state) => {
        state.submitting = true;
      })
      .addCase(logMaterialUsage.fulfilled, (state, action) => {
        state.submitting = false;
        const updated = action.payload;

        state.materials = state.materials.map((mat) =>
          mat._id === updated._id
            ? {
              ...mat,
              ...updated,
              lastUsage: updated.lastUsage || null,        // ✅ persist usage object
              lastUsageCanUndo: updated.lastUsageCanUndo,  // ✅ sync undo flag
            }
            : mat
        );
      })
      .addCase(logMaterialUsage.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })

      // ⏪ Undo Usage
      .addCase(undoMaterialUsage.pending, (state) => {
        state.submitting = true;
      })
      .addCase(undoMaterialUsage.fulfilled, (state, action) => {
        state.submitting = false;
        const updated = action.payload;

        state.materials = state.materials.map((mat) =>
          mat._id === updated._id
            ? {
              ...mat,
              ...updated,
              lastUsage: null,                 // ✅ clear last usage
              lastUsageCanUndo: false,        // ✅ disable undo
            }
            : mat
        );
      })
      .addCase(undoMaterialUsage.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })


      // ⏪ Undo Receive
      .addCase(undoMaterialReceive.pending, (state) => {
        state.submitting = true;
      })
      .addCase(undoMaterialReceive.fulfilled, (state, action) => {
        state.submitting = false;
        const updated = action.payload;
        state.materials = state.materials.map((mat) =>
          mat._id === updated._id
            ? { ...updated, lastReceiveCanUndo: false } // force disable undo
            : mat
        );
      })
      .addCase(undoMaterialReceive.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })


      // ✏️ Edit Material
      .addCase(editMaterialDetails.pending, (state) => {
        state.submitting = true;
      })
      .addCase(editMaterialDetails.fulfilled, (state, action) => {
        state.submitting = false;
        const updated = action.payload;

        // 🔄 Update in materials list
        state.materials = state.materials.map((mat) =>
          mat._id === updated._id ? { ...mat, ...updated } : mat
        );

        // 🔄 If viewing this material, also update detailed state
        if (state.materialDetails?._id === updated._id) {
          state.materialDetails = {
            ...state.materialDetails,
            ...updated,
          };
        }
      })
      .addCase(editMaterialDetails.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })

      .addCase(fetchRecentMaterialActivity.pending, (state) => {
        state.recentActivityLoading = true;
      })
      .addCase(fetchRecentMaterialActivity.fulfilled, (state, action) => {
        state.recentActivityLoading = false;
        state.recentActivity = action.payload.items || [];
        state.recentActivityNextCursor = action.payload.nextCursor || null;
      })
      .addCase(fetchRecentMaterialActivity.rejected, (state, action) => {
        state.recentActivityLoading = false;
        state.error = action.payload;
      })

      // ➕ Load more
      .addCase(fetchMoreMaterialActivity.pending, (state) => {
        state.recentActivityLoading = true;
      })
      .addCase(fetchMoreMaterialActivity.fulfilled, (state, action) => {
        state.recentActivityLoading = false;
        state.recentActivity = [...state.recentActivity, ...(action.payload.items || [])];
        state.recentActivityNextCursor = action.payload.nextCursor || null;
      })
      .addCase(fetchMoreMaterialActivity.rejected, (state, action) => {
        state.recentActivityLoading = false;
        state.error = action.payload;
      })


      // 🗑️ Soft delete -> remove from list
      .addCase(deleteMaterialById.fulfilled, (state, action) => {
        const { materialId } = action.payload;
        state.materials = state.materials.filter(m => m._id !== materialId);
        if (state.materialDetails?._id === materialId) state.materialDetails = null;
      })
      .addCase(deleteMaterialById.rejected, (state, action) => {
        state.error = action.payload;
      })

      // ♻️ Restore -> list will be refreshed by thunk
      .addCase(restoreMaterialById.rejected, (state, action) => {
        state.error = action.payload;
      })

      // 💣 Hard delete -> remove from list
      .addCase(hardDeleteMaterialById.fulfilled, (state, action) => {
        const { materialId } = action.payload;
        state.materials = state.materials.filter(m => m._id !== materialId);
        if (state.materialDetails?._id === materialId) state.materialDetails = null;
      })
      .addCase(hardDeleteMaterialById.rejected, (state, action) => {
        state.error = action.payload;
      });


  },
});

export const {
  clearMaterialError,
  clearSelectedMaterial,
  clearMaterialDetails,
  clearMaterialSubmitState,
  resetRecentMaterialActivity } = materialSlice.actions;
export default materialSlice.reducer;
